import React, { useEffect, useRef, useState } from "react"

interface LandingCountUpProps {
  end: number
  className?: string
  suffix?: string
  duration?: number
  start?: number
  isActive?: boolean
}

const LandingCountUp = React.memo(
  function LandingCountUp({
    end,
    className = "",
    suffix = "",
    duration = 1,
    start = 0,
    isActive = true,
  }: LandingCountUpProps) {
    const [value, setValue] = useState(start)
    const frame = useRef<number | null>(null)

    useEffect(() => {
      if (!isActive) return

      const startTs = performance.now()

      const step = (ts: number) => {
        const progress = Math.min((ts - startTs) / (duration * 1000), 1)
        const current = start + (end - start) * progress
        setValue(current)
        if (progress < 1) {
          frame.current = requestAnimationFrame(step)
        }
      }

      frame.current = requestAnimationFrame(step)
      return () => {
        if (frame.current !== null) cancelAnimationFrame(frame.current)
      }
    }, [end, duration, start, isActive])

    // Format the value and split into head and the last two characters (tail)
    const formatted = Math.round(value).toLocaleString()
    const head = formatted.slice(0, -2)
    const tail = formatted.slice(-2)

    return (
      <span className={className}>
        {head}
        {/* Breathe animation on the last two digits */}
        <span className="inline-block animate-breathe text-white">{tail}</span>
        {suffix}
      </span>
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if these critical props change
    return (
      prevProps.end === nextProps.end &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.start === nextProps.start
    )
  },
)

export default LandingCountUp
